/**
 * Admin · Data Wipe — irreversible full-tenant data reset.
 *
 * Two-step flow: (1) Preview per-collection counts of what will be wiped vs
 * preserved. (2) Type the exact phrase "WIPE ALL TENANT DATA" and re-enter
 * the super-admin's password. Nukes every tenant's accounts / invoices /
 * bills / vouchers / parties / items / gate passes / migration batches /
 * reports so tenants can re-migrate their Tally data with the corrected
 * income/expense classifier. Preserves organizations + users + audit trails.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { AlertTriangle, ShieldAlert, Trash2, RefreshCw, CheckCircle2, Sparkles, Building2, Factory, Bike } from "lucide-react";

export default function AdminDataWipe() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [phrase, setPhrase] = useState("");
  const [password, setPassword] = useState("");
  const [resetCounters, setResetCounters] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  // Iter-119 · Seed-demo-companies state
  const [seedBusy, setSeedBusy] = useState(false);
  const [seedResult, setSeedResult] = useState(null);
  // Iter-137 · Zenith Conveyors flagship-demo state
  const [convBusy, setConvBusy] = useState(false);
  const [convResult, setConvResult] = useState(null);
  // Iter-166 · Honda 2W Dealer demo state
  const [hondaBusy, setHondaBusy] = useState(false);
  const [hondaResult, setHondaResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.post("/admin/data-wipe/preview", {});
      setPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load preview");
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const CONFIRM_PHRASE = "WIPE ALL TENANT DATA";
  const canWipe = phrase.trim() === CONFIRM_PHRASE && password.length >= 6 && !busy;

  const wipe = async () => {
    if (!canWipe) return;
    if (!window.confirm("This will permanently delete every tenant's transactional and master data. Type OK to confirm you understand.")) return;
    setBusy(true);
    setResult(null);
    try {
      const r = await api.post("/admin/data-wipe/execute", {
        password, confirm_phrase: phrase,
        also_reset_org_counters: resetCounters,
      });
      setResult(r.data);
      toast.success(`Wiped ${r.data.totals.docs_deleted.toLocaleString("en-IN")} documents across ${r.data.totals.collections_wiped} collections`);
      setPhrase(""); setPassword("");
      load();  // refresh preview to show 0 counts
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Wipe failed");
    }
    setBusy(false);
  };

  // Iter-119 · Seed 6 demo companies via /platform/seed-demo-companies
  const seedDemo = async () => {
    if (!window.confirm("Seed 6 demo companies (Manufacturing, Trading, Services, Auto Dealership, Retail, Consulting)? Existing names will be skipped.")) return;
    setSeedBusy(true); setSeedResult(null);
    try {
      const r = await api.post("/platform/seed-demo-companies", {}, {
        headers: { "X-Confirm": "SEED-DEMO" },
      });
      setSeedResult(r.data);
      toast.success(`Seeded ${r.data.created?.length || 0} new companies · ${r.data.already_exists?.length || 0} already existed`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Seed failed");
    }
    setSeedBusy(false);
  };

  // Iter-137 · Seed Zenith Conveyors (flagship manufacturing + service demo)
  const seedConveyor = async () => {
    if (!window.confirm("Seed the flagship Zenith Conveyors (Demo) Pvt Ltd company — full P2P + O2C + Mfg cycles + 10-month expense history + 23 bank vouchers? Idempotent — safe if already seeded.")) return;
    setConvBusy(true); setConvResult(null);
    try {
      const r = await api.post("/platform/seed-conveyor-demo", {}, {
        headers: { "X-Confirm": "SEED-CONVEYOR" },
      });
      setConvResult(r.data);
      const s = r.data?.conveyor_seed?.status;
      toast.success(s === "already_seeded"
        ? "Zenith Conveyors already exists — skipped."
        : `Zenith Conveyors seeded · ${r.data?.bank_extras?.bank_vouchers_added || 0} bank vouchers added`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Conveyor seed failed");
    }
    setConvBusy(false);
  };

  // Iter-166 · Seed Honda 2W Dealership demo — flagship Dealership 360 showcase
  const seedHonda = async () => {
    if (!window.confirm("Seed Honda Riders (Demo) Pvt Ltd — the flagship Dealership 360 tenant covering 13 scenarios (leads · quotes · bookings across all payment modes · cancellations · finance × 4 partners · insurance · RTO · service jobs · gate passes · masters · price lists · P2P bills · GL vouchers). Idempotent — safe if already seeded.")) return;
    setHondaBusy(true); setHondaResult(null);
    try {
      const r = await api.post("/platform/seed-honda-demo", {}, {
        headers: { "X-Confirm": "SEED-HONDA" },
      });
      setHondaResult(r.data);
      toast.success("Honda Riders (Demo) seeded — sign in with owner@honda-riders-demo.zenith360.in / Demo@2026");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Honda seed failed");
    }
    setHondaBusy(false);
  };

  return (
    <div className="p-6 lg:p-8 space-y-4 text-foreground" data-testid="admin-data-wipe-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-red-600">// PLATFORM OPS · DESTRUCTIVE</div>
          <h1 className="text-3xl font-bold mt-1 font-heading text-foreground">Data Wipe · Reset every tenant</h1>
          <div className="text-sm text-muted-foreground mt-1 max-w-3xl leading-relaxed">
            Purges every tenant&apos;s transactional and master data across all companies live on this platform.
            Preserves organizations (shells) + users (login) + audit trails so tenants log back in to a
            clean workspace and can re-migrate their Tally data using the corrected income/expense classifier.
          </div>
        </div>
        <Button onClick={load} disabled={loading} variant="outline"
          className="rounded-sm gap-1.5 border-border text-foreground bg-transparent hover:bg-muted" data-testid="refresh-preview">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {/* Danger banner */}
      <div className="border-l-4 border-red-500 bg-red-500/10 p-4 rounded-sm" data-testid="danger-banner">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-6 h-6 text-red-600 shrink-0 mt-0.5" />
          <div className="text-sm leading-relaxed">
            <div className="font-bold text-red-300 mb-1">IRREVERSIBLE OPERATION</div>
            Every accounts, invoices, bills, vouchers, parties, items, gate passes, migration batches,
            fixed assets, reports and every derived record for EVERY tenant on this platform will be
            permanently deleted. Users will be able to log back in, but their workspaces will be empty.
            Only run this immediately before you want tenants to re-import their Tally data.
          </div>
        </div>
      </div>

      {/* Iter-119 · Seed Demo Companies (green-toned counterpart to the red wipe) */}
      <div className="border-l-4 border-emerald-500 bg-emerald-500/10 p-4 rounded-sm" data-testid="seed-demo-panel">
        <div className="flex items-start gap-3">
          <Sparkles className="w-6 h-6 text-emerald-600 shrink-0 mt-0.5" />
          <div className="text-sm leading-relaxed flex-1">
            <div className="font-bold text-emerald-300 mb-1 flex items-center gap-2">
              BOOTSTRAP · SEED 6 DEMO COMPANIES
            </div>
            <div className="text-foreground/80 mb-2">
              Idempotently creates 6 sample tenants covering every entity × industry combination:
              LLP-Manufacturing, PvtLtd-Trading, Proprietorship-Services, PvtLtd-Trading+Dealership,
              PvtLtd-Retail, LLP-Consulting. Each gets a demo owner (<code className="bg-muted px-1 rounded font-mono text-[11px]">owner@{"{industry}"}-demo.zenith360.in</code> / password <code className="bg-muted px-1 rounded font-mono text-[11px]">Demo@2026</code>), a home GSTIN, default branches, doc-type numbering, and 124-164 seeded Chart-of-Accounts entries.
            </div>
            {seedResult && (
              <div className="mt-2 mb-2 text-xs bg-muted rounded-sm p-2 border border-emerald-500/30 max-h-56 overflow-y-auto"
                    data-testid="seed-result">
                <div className="text-emerald-300 font-mono uppercase text-[10px] tracking-widest mb-1">
                  Created {seedResult.created?.length || 0} · Already existed {seedResult.already_exists?.length || 0}
                </div>
                {(seedResult.created || []).map((c) => (
                  <div key={c.org_id} className="flex items-start gap-2 py-1 border-b border-border/60 last:border-none">
                    <Building2 className="w-3 h-3 mt-0.5 text-emerald-600/70 shrink-0" />
                    <div className="flex-1">
                      <div className="text-foreground font-medium">{c.name}</div>
                      <div className="font-mono text-[10px] text-muted-foreground">
                        {c.entity_type} · {c.industry_type} · {c.coa_accounts_seeded} CoA · {c.gstin}
                      </div>
                      <div className="font-mono text-[10px] text-emerald-300">
                        Login: {c.owner_email} / {c.owner_password}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <Button onClick={seedDemo} disabled={seedBusy}
              className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-foreground gap-1.5 font-mono uppercase text-xs tracking-widest"
              data-testid="seed-demo-btn">
              {seedBusy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {seedBusy ? "Seeding…" : "Seed 6 demo companies"}
            </Button>
            <span className="text-[10px] text-muted-foreground ml-2">
              (Idempotent · running twice won't duplicate; existing names are skipped)
            </span>
          </div>
        </div>
      </div>

      {/* Iter-137 · Flagship Zenith Conveyors demo — full mfg + service showcase */}
      <div className="border-l-4 border-accent bg-accent/10 p-4 rounded-sm" data-testid="seed-conveyor-panel">
        <div className="flex items-start gap-3">
          <Factory className="w-6 h-6 text-accent shrink-0 mt-0.5" />
          <div className="text-sm leading-relaxed flex-1">
            <div className="font-bold text-accent mb-1 flex items-center gap-2">
              FLAGSHIP DEMO · ZENITH CONVEYORS (Mfg + Service)
            </div>
            <div className="text-foreground/80 mb-2">
              Idempotently seeds the flagship sales-pitch tenant with the full manufacturing narrative:
              7 role-based users, 8 warehouses, 10 items, 3 BOMs (Steel Sheet · Painted Sheet · Conveyor),
              9 vendors, 4 customers, 5 procurement cycles (PR→PO→Gate→GRN→QC→Bill→Pay), 3 mfg cycles
              with variance accounting, 1 job-work cycle, 2 sales cycles (intra-state + IGST), 10 months
              of expenses + 23 bank vouchers (loan, EMI, GST/TDS deposits, advances). Sales login:{" "}
              <code className="bg-muted px-1 rounded font-mono text-[11px]">owner@conveyor-demo.zenith360.in</code>
              {" "} / <code className="bg-muted px-1 rounded font-mono text-[11px]">Demo@2026</code>.
            </div>
            {convResult && (
              <div className="mt-2 mb-2 text-xs bg-muted rounded-sm p-3 border border-accent/30" data-testid="conveyor-result">
                <div className="text-accent font-mono uppercase text-[10px] tracking-widest mb-2">
                  Status: {convResult.conveyor_seed?.status}
                </div>
                {convResult.conveyor_seed?.status === "seeded" && (
                  <div className="space-y-1 text-foreground/90">
                    <div>Org: <span className="font-mono">{convResult.conveyor_seed.org_id?.slice(0, 8)}…</span> · GSTIN {convResult.conveyor_seed.gstin}</div>
                    <div>Costs: Conveyor ₹{Number(convResult.conveyor_seed.costs?.conveyor_per_unit || 0).toLocaleString("en-IN")} / unit · Sheet ₹{Number(convResult.conveyor_seed.costs?.steel_sheet_per_unit || 0).toLocaleString("en-IN")}</div>
                    <div>Sales: {(convResult.conveyor_seed.sales || []).map((s) => `${s.customer} ${s.conveyors}nos ₹${Number(s.amount).toLocaleString("en-IN")}`).join(" · ")}</div>
                    <div>Bank extras: <span className="font-mono">{convResult.bank_extras?.bank_vouchers_added || 0}</span> vouchers</div>
                  </div>
                )}
                {convResult.conveyor_seed?.status === "already_seeded" && (
                  <div className="text-foreground/80">{convResult.conveyor_seed.note}</div>
                )}
              </div>
            )}
            <Button onClick={seedConveyor} disabled={convBusy}
              className="rounded-sm bg-accent hover:bg-accent/90 text-foreground gap-1.5 font-mono uppercase text-xs tracking-widest"
              data-testid="seed-conveyor-btn">
              {convBusy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Factory className="w-4 h-4" />}
              {convBusy ? "Seeding conveyor demo…" : "Seed Zenith Conveyors demo"}
            </Button>
            <span className="text-[10px] text-muted-foreground ml-2">
              (~30s to run · idempotent · header X-Confirm: SEED-CONVEYOR)
            </span>
          </div>
        </div>
      </div>

      {/* Iter-166 · Honda 2W Dealership demo — flagship Dealership 360 showcase */}
      <div className="border-l-4 border-red-400 bg-red-400/10 p-4 rounded-sm" data-testid="seed-honda-panel">
        <div className="flex items-start gap-3">
          <Bike className="w-6 h-6 text-red-600 shrink-0 mt-0.5" />
          <div className="text-sm leading-relaxed flex-1">
            <div className="font-bold text-red-300 mb-1 flex items-center gap-2">
              FLAGSHIP DEALERSHIP DEMO · HONDA RIDERS (2W)
            </div>
            <div className="text-foreground/80 mb-2">
              Idempotently seeds the Dealership 360 showcase tenant: 5 role-based users
              (owner · sales · service · cashier · accountant), 5 Honda models × 15 variants,
              9 bookings across all payment modes (cash · UPI · NEFT · cheque · finance),
              3 cancellation variants, waitlist, finance × 4 partners, insurance × 3 types,
              accessories + EW, RTO applications, 4 job cards, service reminders, gate passes,
              masters, price lists, full PR → PO → GRN → Bill → Payment cycle with GL vouchers,
              cash desk pending + collected, discount approvals. Sales pitch login:{" "}
              <code className="bg-muted px-1 rounded font-mono text-[11px]">owner@honda-riders-demo.zenith360.in</code>
              {" "} / <code className="bg-muted px-1 rounded font-mono text-[11px]">Demo@2026</code>.
            </div>
            {hondaResult && (
              <div className="mt-2 mb-2 text-xs bg-muted rounded-sm p-3 border border-red-400/30 max-h-64 overflow-y-auto" data-testid="honda-result">
                <div className="text-red-300 font-mono uppercase text-[10px] tracking-widest mb-2">
                  Status: {hondaResult.status} · Org {hondaResult.org_id?.slice(0, 8)}…
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-foreground/90 font-mono text-[11px]">
                  {Object.entries(hondaResult.counts || {}).map(([k, v]) => (
                    <div key={k}><span className="text-muted-foreground">{k.replace(/_/g, " ")}:</span> <b>{v}</b></div>
                  ))}
                </div>
                <div className="mt-2 text-red-300">
                  Login: <span className="font-mono">{hondaResult.login}</span> / <span className="font-mono">{hondaResult.password}</span>
                </div>
              </div>
            )}
            <Button onClick={seedHonda} disabled={hondaBusy}
              className="rounded-sm bg-red-500 hover:bg-red-600 text-foreground gap-1.5 font-mono uppercase text-xs tracking-widest"
              data-testid="seed-honda-btn">
              {hondaBusy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Bike className="w-4 h-4" />}
              {hondaBusy ? "Seeding Honda demo…" : "Seed Honda 2W Dealer demo"}
            </Button>
            <span className="text-[10px] text-muted-foreground ml-2">
              (~10s to run · idempotent · header X-Confirm: SEED-HONDA)
            </span>
          </div>
        </div>
      </div>

      {/* Preview totals */}
      {preview && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            ["Documents to delete", preview.totals.docs_to_delete, "text-red-600"],
            ["Collections wiped", preview.totals.collections_to_wipe, "text-red-600"],
            ["Collections preserved", preview.totals.collections_preserved, "text-emerald-600"],
            ["Orgs surviving", preview.totals.orgs_surviving, "text-emerald-600"],
          ].map(([lbl, val, tone]) => (
            <div key={lbl} className="border border-border bg-card rounded-sm p-3">
              <div className="text-[10px] font-mono uppercase text-muted-foreground tracking-widest">{lbl}</div>
              <div className={`text-2xl font-bold mt-1 font-mono ${tone}`}>{Number(val).toLocaleString("en-IN")}</div>
            </div>
          ))}
        </div>
      )}

      {/* Confirmation form */}
      <div className="border border-red-500/40 bg-red-500/5 rounded-sm p-4 space-y-3" data-testid="wipe-form">
        <div className="flex items-center gap-2 text-red-300 font-mono text-[10px] uppercase tracking-widest">
          <ShieldAlert className="w-4 h-4" /> Confirmation required
        </div>
        <div>
          <Label className="text-[11px] uppercase font-mono text-foreground/80">
            Type the phrase exactly: <span className="text-red-300 font-bold">{CONFIRM_PHRASE}</span>
          </Label>
          <Input value={phrase} onChange={(e) => setPhrase(e.target.value)}
            placeholder={CONFIRM_PHRASE}
            className="mt-1 h-10 bg-muted border-border text-foreground font-mono text-sm"
            data-testid="wipe-phrase-input" />
        </div>
        <div>
          <Label className="text-[11px] uppercase font-mono text-foreground/80">
            Re-enter your super-admin password
          </Label>
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="Your login password"
            className="mt-1 h-10 bg-muted border-border text-foreground text-sm"
            data-testid="wipe-password-input" />
        </div>
        <label className="flex items-center gap-2 text-sm text-foreground/80 select-none">
          <input type="checkbox" checked={resetCounters}
            onChange={(e) => setResetCounters(e.target.checked)}
            data-testid="wipe-reset-counters" />
          Also reset per-org voucher-number counters + usage snapshots
        </label>
        <div className="pt-1">
          <Button onClick={wipe} disabled={!canWipe}
            className="rounded-sm bg-red-600 hover:bg-red-700 text-foreground gap-1.5 font-mono uppercase text-xs tracking-widest"
            data-testid="execute-wipe">
            {busy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            {busy ? "Wiping…" : "Execute data wipe"}
          </Button>
        </div>
      </div>

      {/* Post-wipe result */}
      {result && (
        <div className="border border-emerald-500/40 bg-emerald-500/5 rounded-sm p-4 space-y-2" data-testid="wipe-result">
          <div className="flex items-center gap-2 text-emerald-300 font-mono text-[10px] uppercase tracking-widest">
            <CheckCircle2 className="w-4 h-4" /> Wipe complete
          </div>
          <div className="text-sm text-foreground/90">
            {result.totals.docs_deleted.toLocaleString("en-IN")} documents deleted across
            {" "}{result.totals.collections_wiped} collections. Audit id
            <span className="font-mono ml-1 text-emerald-600">{result.audit_id.slice(0, 8)}</span>.
          </div>
          {result.totals.errors > 0 && (
            <div className="text-red-600 text-xs font-mono">
              {result.totals.errors} collection(s) failed — check the audit log
            </div>
          )}
        </div>
      )}

      {/* Two-column collection lists */}
      {preview && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="border border-red-500/20 rounded-sm" data-testid="to-wipe-list">
            <div className="px-3 py-2 border-b border-border bg-red-500/10 text-[11px] font-mono uppercase tracking-widest text-red-300 flex items-center gap-2">
              <Trash2 className="w-3.5 h-3.5" /> Collections to WIPE · {preview.to_wipe.length}
            </div>
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full text-xs">
                <tbody className="divide-y divide-white/5">
                  {preview.to_wipe.map((r) => (
                    <tr key={r.collection}>
                      <td className="px-3 py-1.5 font-mono text-foreground/80">{r.collection}</td>
                      <td className="px-3 py-1.5 text-right font-mono text-red-300 tabular-nums">
                        {r.count.toLocaleString("en-IN")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="border border-emerald-500/20 rounded-sm" data-testid="to-preserve-list">
            <div className="px-3 py-2 border-b border-border bg-emerald-500/10 text-[11px] font-mono uppercase tracking-widest text-emerald-300 flex items-center gap-2">
              <CheckCircle2 className="w-3.5 h-3.5" /> Collections PRESERVED · {preview.to_preserve.length}
            </div>
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full text-xs">
                <tbody className="divide-y divide-white/5">
                  {preview.to_preserve.map((r) => (
                    <tr key={r.collection}>
                      <td className="px-3 py-1.5 font-mono text-foreground/80">{r.collection}</td>
                      <td className="px-3 py-1.5 text-right font-mono text-emerald-300 tabular-nums">
                        {r.count.toLocaleString("en-IN")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
